import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Eyebrow } from "../components/Primitives";
import AppShell from "../components/AppShell";
import { useAccount } from "../account/AccountContext";
import { accountsApi, formatAuthError } from "../lib/accountsApi";

export default function Settings() {
    const { account, loading, logout } = useAccount();
    const navigate = useNavigate();
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [deleteStep, setDeleteStep] = useState(0); // 0 closed, 1 explain, 2 confirm
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);

    if (loading) return <AppShell><div className="flex-1 flex items-center justify-center text-ink-muted">Loading…</div></AppShell>;

    if (!account) {
        return (
            <AppShell>
                <div className="flex-1 flex flex-col justify-center px-7" data-testid="settings-signin-prompt">
                    <Eyebrow>Settings</Eyebrow>
                    <h1 className="mt-2 font-display text-4xl text-burgundy">Sign <span className="italic">in</span> first.</h1>
                    <p className="mt-4 text-sm text-ink-muted">You need to be signed in to manage your account and privacy.</p>
                    <div className="mt-7">
                        <Btn data-testid="settings-signin-btn" onClick={() => navigate("/login")}>Sign in</Btn>
                    </div>
                </div>
            </AppShell>
        );
    }

    const downloadData = async () => {
        setExporting(true);
        setExportError(null);
        try {
            const res = await accountsApi.dataExport();
            if (res.ok === false) {
                setExportError(res.error || "Could not send the export email. Please try again later.");
                return;
            }
            toast.success(`Your data export was emailed to ${res.sent_to}`);
        } catch (e) {
            setExportError(formatAuthError(e?.response?.data?.detail));
        } finally {
            setExporting(false);
        }
    };

    const confirmDelete = async () => {
        setDeleting(true);
        setError(null);
        try {
            await accountsApi.deleteAccount(confirmText);
            await logout();
            toast.success("Your account has been deleted.");
            navigate("/", { replace: true });
        } catch (e) {
            setError(formatAuthError(e?.response?.data?.detail));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col px-7 py-10" data-testid="settings-screen">
                <button onClick={() => navigate(-1)} data-testid="settings-back-btn" className="self-start tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">
                    ← Back
                </button>
                <Eyebrow className="mt-6">Settings</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                    <span className="italic">Privacy</span> &amp; data
                </h1>
                <p className="mt-3 text-sm text-ink-muted">Signed in as <span className="text-burgundy font-medium">{account.email}</span></p>

                <div className="mt-8 space-y-3">
                    <div className="border border-burgundy/15 bg-cream px-5 py-4" data-testid="settings-download-data">
                        <div className="font-display text-lg text-burgundy">Download my data</div>
                        <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                            We'll email you a JSON export of the personal data associated with your account.
                        </p>
                        <button
                            data-testid="download-data-btn"
                            onClick={downloadData}
                            disabled={exporting}
                            className="mt-3 text-[10px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4 disabled:opacity-50"
                        >
                            {exporting ? "Sending…" : "Email my data"}
                        </button>
                        {exportError && (
                            <div data-testid="export-error" className="mt-2 text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                                {exportError}
                            </div>
                        )}
                    </div>

                    <div className="border border-burgundy/25 bg-cream px-5 py-4" data-testid="settings-delete-account">
                        <div className="font-display text-lg text-burgundy">Delete account</div>
                        <p className="mt-1 text-xs text-ink-muted leading-relaxed">
                            Permanently delete your Ever After account and personal data.
                        </p>
                        <button
                            data-testid="delete-account-btn"
                            onClick={() => { setDeleteStep(1); setConfirmText(""); setError(null); }}
                            className="mt-3 text-[10px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4"
                        >
                            Delete my account
                        </button>
                    </div>
                </div>

                {deleteStep > 0 && (
                    <div className="fixed inset-0 bg-black/50 z-[70] flex items-end sm:items-center justify-center px-3 pb-3 sm:p-6"
                        onClick={() => setDeleteStep(0)} data-testid="delete-modal">
                        <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-md bg-cream border border-burgundy/20 max-h-[85vh] flex flex-col overflow-y-auto">
                            {deleteStep === 1 ? (
                                <div className="px-6 py-6" data-testid="delete-step-1">
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Delete account · Step 1 of 2</div>
                                    <h3 className="mt-2 font-display text-2xl text-burgundy"><span className="italic">Before</span> you go.</h3>
                                    <div className="mt-4 text-sm text-ink leading-relaxed space-y-3">
                                        <p><span className="font-semibold text-burgundy">What gets deleted:</span> your profile, assessment answers and archetype results, photos, AI conversations, notifications, and sign-in credentials. You lose access immediately, and personal data is permanently erased within 30 days.</p>
                                        <p><span className="font-semibold text-burgundy">What is retained:</span> transaction records are kept for 7 years as required by law, and safety-related records (reports, blocks, and date check-ins) are retained to protect our community.</p>
                                    </div>
                                    <div className="mt-6 space-y-2">
                                        <Btn data-testid="delete-continue-btn" onClick={() => setDeleteStep(2)}>Continue</Btn>
                                        <button data-testid="delete-cancel-btn" onClick={() => setDeleteStep(0)}
                                            className="block w-full text-center text-[10px] tracking-[0.32em] uppercase text-ink-muted py-2">
                                            Keep my account
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="px-6 py-6" data-testid="delete-step-2">
                                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Delete account · Step 2 of 2</div>
                                    <h3 className="mt-2 font-display text-2xl text-burgundy"><span className="italic">Type</span> DELETE to confirm.</h3>
                                    <p className="mt-3 text-sm text-ink-muted">This cannot be undone. A confirmation email will be sent to {account.email}.</p>
                                    <input
                                        data-testid="delete-confirm-input"
                                        type="text"
                                        autoFocus
                                        placeholder="DELETE"
                                        value={confirmText}
                                        onChange={e => { setConfirmText(e.target.value); setError(null); }}
                                        className="mt-4 w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base tracking-[0.2em] text-ink focus:outline-none"
                                    />
                                    {error && (
                                        <div data-testid="delete-error" className="mt-3 text-sm text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">{error}</div>
                                    )}
                                    <div className="mt-5 space-y-2">
                                        <Btn
                                            data-testid="delete-confirm-btn"
                                            onClick={confirmDelete}
                                            disabled={confirmText.trim().toUpperCase() !== "DELETE" || deleting}
                                            className={confirmText.trim().toUpperCase() !== "DELETE" || deleting ? "opacity-50 cursor-not-allowed" : ""}
                                        >
                                            {deleting ? "Deleting…" : "Permanently delete my account"}
                                        </Btn>
                                        <button data-testid="delete-cancel-btn-2" onClick={() => setDeleteStep(0)}
                                            className="block w-full text-center text-[10px] tracking-[0.32em] uppercase text-ink-muted py-2">
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <p className="mt-8 text-[10px] tracking-[0.28em] uppercase text-ink-light">
                    Not signed in on this device?{" "}
                    <Link to="/delete-account" data-testid="settings-public-delete-link" className="underline underline-offset-4 hover:text-burgundy">
                        Request deletion by email
                    </Link>
                </p>
            </div>
        </AppShell>
    );
}
